import { Handle, Position, type NodeProps } from '@xyflow/react';
import { motion } from 'framer-motion';
import { BarChart3, Code, Edit, ExternalLink, Globe, Link2, Plus, Trash2, TrendingDown, TrendingUp } from 'lucide-react';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import type { UpsellNodeData } from '../utils/funnelTypes';

function UpsellNodeComponent({ data, selected }: NodeProps) {
  const { t } = useTranslation('funnel');
  const nodeData = data as unknown as UpsellNodeData;
  const isUpsell = nodeData.funnelType === 'up_sell';
  const Icon = isUpsell ? TrendingUp : TrendingDown;
  const hasImage = !!nodeData.imageUrl;

  const acceptText = nodeData.upSellDisplayText || t('accept_default_text');
  const acceptBgColor = nodeData.upSellButtonColor || '#5CCE5E';
  const acceptTextColor = nodeData.upSellDisplayTextColor || '#000000';
  const declineText = nodeData.downSellDisplayText || t('decline_default_text');
  const declineBgColor = nodeData.downSellButtonColor || '#DC2626';
  const declineTextColor = nodeData.downSellDisplayTextColor || '#FFFFFF';

  const acceptConnected = nodeData.hasAcceptTarget;
  const acceptExternal = nodeData.hasExternalAccept;
  const acceptFree = !acceptConnected && !acceptExternal;

  const declineConnected = nodeData.hasDeclineTarget;
  const declineExternal = nodeData.hasExternalDecline;
  const declineFree = !declineConnected && !declineExternal;

  const hasConversions = nodeData.conversions != null && nodeData.conversions > 0;
  const hasRevenue = !!nodeData.revenue;

  const truncateUrl = (url: string) => {
    try {
      const parsed = new URL(url);
      return parsed.hostname + (parsed.pathname.length > 15 ? parsed.pathname.slice(0, 15) + '…' : parsed.pathname);
    } catch {
      return url.length > 30 ? url.slice(0, 30) + '…' : url;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4, delay: 0.25, ease: 'easeOut' }}
      className={`
        relative rounded-xl border bg-[#18181B]/95 backdrop-blur-md shadow-xl
        min-w-[280px] max-w-[320px] overflow-hidden transition-all duration-200 group
        ${selected
          ? (isUpsell ? 'border-emerald-500 shadow-emerald-500/20' : 'border-rose-500 shadow-rose-500/20')
          : (isUpsell ? 'border-[#27272a] hover:border-emerald-500/40' : 'border-[#27272a] hover:border-rose-500/40')
        }
      `}
    >
      <div className={`h-0.5 w-full bg-gradient-to-r ${
        isUpsell ? 'from-emerald-500/60 via-emerald-400 to-emerald-500/60' : 'from-rose-500/60 via-rose-400 to-rose-500/60'
      }`} />

      {/* Preview browser do upsell */}
      <div className="mx-3 mt-3 rounded-lg border border-[#27272a] bg-[#09090b] overflow-hidden">
        <div className="flex items-center gap-1.5 px-2 py-1 bg-[#111113] border-b border-[#27272a]">
          <div className="flex gap-0.5">
            <span className="h-1.5 w-1.5 rounded-full bg-red-500/60" />
            <span className="h-1.5 w-1.5 rounded-full bg-yellow-500/60" />
            <span className="h-1.5 w-1.5 rounded-full bg-green-500/60" />
          </div>
          {/* URL da página clicável */}
          <a
            href={nodeData.page}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="flex-1 flex items-center gap-1 rounded bg-[#1a1a1d] px-1.5 py-0.5 ml-1 hover:bg-[#222226] group/url transition-colors"
            title={nodeData.page}
          >
            <Globe className="h-2 w-2 text-zinc-600 flex-shrink-0 group-hover/url:text-violet-400 transition-colors" />
            <span className="text-[7px] text-zinc-500 truncate group-hover/url:text-violet-400 transition-colors">{truncateUrl(nodeData.page)}</span>
            <ExternalLink className="h-2 w-2 text-zinc-700 flex-shrink-0 opacity-0 group-hover/url:opacity-100 group-hover/url:text-violet-400 transition-all ml-auto" />
          </a>
        </div>

        <div className="p-3 space-y-2">
          <div className="text-center">
            <p className="text-[9px] text-zinc-500 mb-1">{t('offer')}</p>
            <p className="text-[10px] font-medium text-white truncate">{nodeData.offerName}</p>
            <p className={`text-sm font-bold mt-0.5 ${isUpsell ? 'text-emerald-400' : 'text-rose-400'}`}>
              {nodeData.priceFormatted}
            </p>
          </div>

          {/* Botões do upsell */}
          <div className="space-y-1.5">
            {/* Botão Aceitar */}
            <div className="relative">
              <button
                data-action="add-upsell-accept"
                data-upsell-id={nodeData.upsellId}
                data-funnel-type="up_sell"
                className="w-full py-2 rounded-md text-[10px] font-medium transition-all hover:brightness-110 cursor-pointer"
                style={{
                  backgroundColor: acceptBgColor,
                  color: acceptTextColor,
                }}
              >
                <span className="pointer-events-none">{acceptText}</span>
              </button>

              {/* Overlay de "adicionar" */}
              {acceptFree && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-md opacity-0 hover:opacity-100 transition-opacity pointer-events-none">
                  <Plus className="h-4 w-4 text-white" />
                </div>
              )}

              {/* Indicador de status — mais visível com background */}
              {acceptConnected && (
                <div className="absolute top-0.5 right-0.5 flex h-5 w-5 items-center justify-center rounded bg-emerald-500/20 ring-1 ring-emerald-500/40">
                  <Link2 className="h-3 w-3 text-emerald-300" />
                </div>
              )}
              {acceptExternal && (
                <div className="absolute top-0.5 right-0.5 flex h-5 w-5 items-center justify-center rounded bg-blue-500/20 ring-1 ring-blue-500/40">
                  <ExternalLink className="h-3 w-3 text-blue-300" />
                </div>
              )}
            </div>

            {/* Botão Recusar */}
            <div className="relative">
              <button
                data-action="add-upsell-decline"
                data-upsell-id={nodeData.upsellId}
                data-funnel-type="down_sell"
                className="w-full py-2 rounded-md text-[10px] font-medium transition-all hover:brightness-110 cursor-pointer"
                style={{
                  backgroundColor: declineBgColor,
                  color: declineTextColor,
                }}
              >
                <span className="pointer-events-none">{declineText}</span>
              </button>

              {declineFree && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-md opacity-0 hover:opacity-100 transition-opacity pointer-events-none">
                  <Plus className="h-4 w-4 text-white" />
                </div>
              )}
              {declineConnected && (
                <div className="absolute top-0.5 right-0.5 flex h-5 w-5 items-center justify-center rounded bg-rose-500/20 ring-1 ring-rose-500/40">
                  <Link2 className="h-3 w-3 text-rose-300" />
                </div>
              )}
              {declineExternal && (
                <div className="absolute top-0.5 right-0.5 flex h-5 w-5 items-center justify-center rounded bg-blue-500/20 ring-1 ring-blue-500/40">
                  <ExternalLink className="h-3 w-3 text-blue-300" />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Rodapé do card */}
      <div className="p-3">
        <div className="flex items-center gap-3">
          {hasImage ? (
            <img
              src={nodeData.imageUrl}
              alt={nodeData.label}
              className={`h-8 w-8 rounded-lg object-cover ring-1 ${isUpsell ? 'ring-emerald-500/30' : 'ring-rose-500/30'}`}
            />
          ) : (
            <div className={`flex h-8 w-8 items-center justify-center rounded-lg ring-1 ${
              isUpsell ? 'bg-emerald-500/15 ring-emerald-500/30' : 'bg-rose-500/15 ring-rose-500/30'
            }`}>
              <Icon className={`h-4 w-4 ${isUpsell ? 'text-emerald-400' : 'text-rose-400'}`} />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className={`text-[9px] font-medium uppercase tracking-wider ${isUpsell ? 'text-emerald-400/70' : 'text-rose-400/70'}`}>
              {isUpsell ? t('upsell') : t('downsell')}
            </p>
            <p className="text-xs font-semibold text-white truncate">{nodeData.label}</p>
          </div>

          {/* Botões de ação — Editar e Excluir */}
          <div className="flex items-center gap-0.5">
            <button
              data-action="edit"
              data-node-type="upsell"
              className={`flex h-6 w-6 items-center justify-center rounded-md transition-colors ${isUpsell ? 'hover:bg-emerald-500/20' : 'hover:bg-rose-500/20'}`}
              title={t('edit')}
            >
              <Edit className={`h-3 w-3 pointer-events-none ${isUpsell ? 'text-emerald-400' : 'text-rose-400'}`} />
            </button>
            <button
              data-action="delete"
              data-node-type="upsell"
              className="flex h-6 w-6 items-center justify-center rounded-md hover:bg-red-500/20 transition-colors"
              title={t('delete')}
            >
              <Trash2 className="h-3 w-3 text-red-400 pointer-events-none" />
            </button>
          </div>
        </div>

        {/* Stats de conversão — só exibe quando tem vendas ou revenue real */}
        {(hasConversions || hasRevenue) && (
          <div className={`mt-2 flex items-center gap-2 rounded-md px-2 py-1 ${
            isUpsell ? 'bg-emerald-500/5 border border-emerald-500/10' : 'bg-rose-500/5 border border-rose-500/10'
          }`}>
            <BarChart3 className={`h-3 w-3 flex-shrink-0 ${isUpsell ? 'text-emerald-400' : 'text-rose-400'}`} />
            {hasConversions && (
              <span className={`text-[9px] ${isUpsell ? 'text-emerald-300' : 'text-rose-300'}`}>
                {nodeData.conversions} {t('sales')}
              </span>
            )}
            {hasRevenue && (
              <span className={`text-[9px] font-semibold ${isUpsell ? 'text-emerald-400' : 'text-rose-400'}`}>
                {nodeData.revenue}
              </span>
            )}
          </div>
        )}

        {/* Botão de script */}
        <button
          data-action="view-script"
          data-upsell-id={nodeData.upsellId}
          className={`mt-2 w-full flex items-center justify-center gap-1.5 rounded-md border px-2 py-1.5 text-[9px] font-medium transition-colors ${
            isUpsell
              ? 'bg-emerald-500/5 border-emerald-500/15 text-emerald-400 hover:bg-emerald-500/15'
              : 'bg-rose-500/5 border-rose-500/15 text-rose-400 hover:bg-rose-500/15'
          }`}
        >
          <Code className="h-3 w-3 pointer-events-none" />
          {t('view_script')}
        </button>

        {/* Indicadores de status dos handles */}
        <div className="mt-2 flex items-center justify-between">
          <div className="flex items-center gap-1">
            <span className={`h-2 w-2 rounded-full ${acceptConnected ? 'bg-emerald-500' : acceptExternal ? 'bg-blue-500' : 'bg-emerald-500/40 ring-1 ring-dashed ring-emerald-500/50'}`} />
            <span className="text-[9px] text-zinc-500">
              {acceptConnected ? t('accept') : acceptExternal ? t('ext') : t('accept')}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-[9px] text-zinc-500">
              {declineConnected ? t('decline') : declineExternal ? t('ext') : t('decline')}
            </span>
            <span className={`h-2 w-2 rounded-full ${declineConnected ? 'bg-red-500' : declineExternal ? 'bg-blue-500' : 'bg-red-500/40 ring-1 ring-dashed ring-red-500/50'}`} />
          </div>
        </div>
      </div>

      <Handle
        type="target"
        position={Position.Top}
        className={`!w-2.5 !h-2.5 !border-2 !border-[#18181B] !-top-1.5 ${isUpsell ? '!bg-emerald-500' : '!bg-rose-500'}`}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="accept"
        style={{ left: '30%' }}
        className="!w-2.5 !h-2.5 !bg-emerald-500 !border-2 !border-[#18181B] !-bottom-1.5"
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="decline"
        style={{ left: '70%' }}
        className="!w-2.5 !h-2.5 !bg-red-500 !border-2 !border-[#18181B] !-bottom-1.5"
      />

      {/* Handles laterais invisíveis para back-edges (ciclos) */}
      <Handle type="target" position={Position.Left} id="target-left"
        className={`!w-1.5 !h-1.5 !border !border-[#18181B] !opacity-0 ${isUpsell ? '!bg-emerald-500' : '!bg-rose-500'}`}
      />
      <Handle type="target" position={Position.Right} id="target-right"
        className={`!w-1.5 !h-1.5 !border !border-[#18181B] !opacity-0 ${isUpsell ? '!bg-emerald-500' : '!bg-rose-500'}`}
      />
      <Handle type="source" position={Position.Left} id="source-left"
        className="!w-1.5 !h-1.5 !border !border-[#18181B] !opacity-0 !bg-zinc-500"
      />
      <Handle type="source" position={Position.Right} id="source-right"
        className="!w-1.5 !h-1.5 !border !border-[#18181B] !opacity-0 !bg-zinc-500"
      />
    </motion.div>
  );
}

export const UpsellNode = memo(UpsellNodeComponent);
